import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { organizationSelectors } from 'store/selectors/organizations';
import { campaignsSelectors } from 'store/selectors/campaigns';

import { useCopyToClipboard } from 'utils/hooks';
import { StyledCode } from 'globalStyles';
import { ActionCenterWidget, Callout, WidgetCard, WidgetInstructions } from 'common/components';
import WidgetDialog from '../WidgetDialog';

const WidgetWrappers = styled.section`
  display: grid;
  gap: 0 16px;
  grid-template-columns: 1fr 1fr 1fr;
  margin-top: 20px;
`;

const DRAFT_ACTION_CENTER_MESSAGE =
  'Widget forms will show an error if embedded until the action center is published.';

const WidgetsTab = ({ publishStatus }) => {
  const { actionCenterUuid } = useParams();
  const [copy] = useCopyToClipboard();

  const {
    actionCenterDetails,
    organization: { flags },
  } = useSelector(state => ({
    actionCenterDetails: campaignsSelectors.getActionCenterDetails(state),
    organization: organizationSelectors.getOrganization(state),
  }));

  const [dialogData, setDialogData] = useState({
    isDialogOpen: false,
    widgetCode: '',
  });

  const {
    REACT_APP_LANDING_PAGE_URL,
    REACT_APP_WIDGET_URL,
    REACT_APP_WIDGET_V2_URL,
    REACT_APP_WIDGET_V4_URL,
  } = process.env;

  const widgetCodeV1 = `
    <div
      data-action-center-id="${actionCenterUuid}"
      data-domain="${REACT_APP_LANDING_PAGE_URL}"
      data-height="1000"
      id="muster-action-center"
    >
      <script src="${REACT_APP_WIDGET_URL}"></script>
    </div>
  `;

  const widgetCodeV2 = `
    <div
      class="muster-embed muster-embed-action"
      data-action-center-id="${actionCenterUuid}"
      data-domain="${REACT_APP_LANDING_PAGE_URL}"
      data-height="1000"
      data-width="100%"
      data-height-full="true"
      data-css=""
      id="muster-widget-v2-action-center-${actionCenterUuid}"
    >
      <script
        src="${REACT_APP_WIDGET_V2_URL}"
        widget-id="muster-widget-v2-action-center-${actionCenterUuid}"
      ></script>
    </div>
  `;

  const widgetCodeV4 = `
    <div
      class="muster-embed muster-embed-action"
      data-action-center-id="${actionCenterUuid}"
      data-domain="${REACT_APP_LANDING_PAGE_URL}"
      data-height="1000"
      data-width="100%"
      data-height-full="true"
      data-css=""
      id="muster-action-embed-${actionCenterUuid}"
    >
      <script
        src="${REACT_APP_WIDGET_V4_URL}"
        widget-id="muster-action-embed-${actionCenterUuid}"
      ></script>
    </div>
  `;

  const handleOpenModal = widgetCode => {
    setDialogData({
      isDialogOpen: true,
      widgetCode,
    });
  };

  const showLegacyWidget = REACT_APP_WIDGET_URL && !!flags.showMessageMapping;

  const draftStatus = publishStatus === 1;

  const useLatestVersion = useMemo(
    () => actionCenterDetails?.version === 'version4',
    [actionCenterDetails?.version]
  );

  const handleCopyToClipboard = () => {
    copy(dialogData.widgetCode, 'textarea', 'Code');
    setDialogData({
      isDialogOpen: false,
      widgetCode: '',
    });
  };

  return (
    <>
      <WidgetWrappers aria-label="Widgets Code Snippets">
        {useLatestVersion ? (
          <ActionCenterWidget
            defaultActionCenterId={actionCenterUuid}
            showActionCenterSelect={false}
          />
        ) : (
          <>
            {REACT_APP_WIDGET_V4_URL && (
              <WidgetCard
                draftStatus={draftStatus}
                draftMessage={DRAFT_ACTION_CENTER_MESSAGE}
                imageSrc="/images/widget-preview-elegante.webp"
                imageAlt="Elegante widget preview"
                title="Elegante"
                subtitle="(Summer 2025)"
                widgetCode={widgetCodeV4}
                onImageClick={handleOpenModal}
              />
            )}
            {REACT_APP_WIDGET_V2_URL && (
              <WidgetCard
                draftStatus={draftStatus}
                draftMessage={DRAFT_ACTION_CENTER_MESSAGE}
                imageSrc="/images/widget-preview-minima.png"
                imageAlt="Minima widget preview"
                title="Minima"
                widgetCode={widgetCodeV2}
                onImageClick={handleOpenModal}
              />
            )}
            {showLegacyWidget && (
              <WidgetCard
                draftStatus={draftStatus}
                draftMessage={DRAFT_ACTION_CENTER_MESSAGE}
                imageSrc="/images/widget-preview-aesthete.png"
                imageAlt="Aesthete widget preview"
                title="Aesthete"
                subtitle="(Deprecated - see below)"
                widgetCode={widgetCodeV1}
                onImageClick={handleOpenModal}
              />
            )}
          </>
        )}
      </WidgetWrappers>
      <WidgetInstructions widgetType="action center form" isDraft={draftStatus} />
      {showLegacyWidget && !useLatestVersion && (
        <Callout severity="warning" title="The Aesthete widget is deprecated!">
          <p>
            The Aesthete widget is a legacy widget and we are planning to remove it as an option in
            the future. It does not support phone call actions, message mapping, or any features
            added since its introduction.
          </p>
          <p>
            Removal of the widget option HERE will have NO EFFECT on published widgets! Published
            widgets will continue to work even after the option is removed here.
          </p>
        </Callout>
      )}
      <WidgetDialog
        isDialogOpen={dialogData.isDialogOpen}
        handleDialogClose={() => setDialogData({ isDialogOpen: false })}
        title="Widget Code"
        handleCopyToClipboard={handleCopyToClipboard}
      >
        <StyledCode>{dialogData.widgetCode}</StyledCode>
      </WidgetDialog>
    </>
  );
};

WidgetsTab.defaultProps = {
  publishStatus: null,
};

WidgetsTab.propTypes = {
  publishStatus: PropTypes.number,
};

export default WidgetsTab;
